import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import logo from "../assets/logo-mark.svg";
import classNames from "../utils/classNames";
import { Badge } from "../components/badge";

export default function DesktopNavigation({ navOptions, orgName }) {
  const getActiveParent = (options) =>
    options.find((item) => item.children?.some((child) => child.current))
      ?.name ?? null;

  const [expanded, setExpanded] = useState(() => getActiveParent(navOptions));

  useEffect(() => {
    const activeParent = getActiveParent(navOptions);
    if (activeParent) setExpanded(activeParent);
  }, [navOptions]);

  const handleToggle = (itemName) => {
    setExpanded((prev) => (prev === itemName ? null : itemName));
  };

  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col bg-gray-50 dark:bg-gray-800">
      <div className="flex flex-col h-full">
        <div className="flex items-center pt-5 pl-6 pb-12 mb-2">
          <Link to="/">
            <img className="h-6 w-auto" src={logo} alt="Opspot" />
          </Link>
        </div>

        {/* Main navigation */}
        <nav className="flex-1">
          <ul role="list" className="flex flex-col">
            {navOptions.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = expanded === item.name;

              return (
                <React.Fragment key={item.name}>
                  <li
                    className={`${
                      item.separator
                        ? "border-t border-gray-100 dark:border-gray-700 mt-4 pt-6"
                        : ""
                    } flex items-center mx-4`}
                  >
                    {hasChildren ? (
                      <button
                        type="button"
                        onClick={() => handleToggle(item.name)}
                        className={classNames(
                          item.current && !hasChildren
                            ? "text-brand-primary font-semibold"
                            : "font-medium text-gray-600 hover:text-gray-800 hover:font-semibold dark:text-gray-300 dark:hover:text-gray-100 transition-colors duration-200",
                          "group flex items-center mb-1 gap-x-3 p-2 rounded-md text-sm leading-6 tracking-wide w-full justify-between",
                        )}
                      >
                        <div className="flex items-center gap-x-3">
                          {item.icon && (
                            <item.icon
                              className="h-5 w-5 shrink-0 text-gray-700 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
                              aria-hidden="true"
                            />
                          )}
                          <span>{item.name}</span>
                          {item.badge && (
                            <Badge variant={item.badge.variant}>
                              {item.badge.text}
                            </Badge>
                          )}
                        </div>
                        {isExpanded ? (
                          <ChevronUpIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3 text-gray-600 dark:text-gray-400" />
                        )}
                      </button>
                    ) : (
                      <Link
                        to={item.href}
                        className={classNames(
                          item.current && !hasChildren
                            ? "bg-black/[0.025] dark:bg-white/[0.025] text-gray-700 dark:text-gray-100 font-semibold"
                            : "font-medium text-gray-600 hover:text-gray-800 hover:font-semibold dark:text-gray-300 dark:hover:text-gray-100 transition-colors duration-200",
                          "group flex items-center mb-1 gap-x-3 px-2 py-1.5 rounded-md text-sm leading-6 tracking-wide w-full",
                        )}
                      >
                        {item.icon && (
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-brand-primary"
                                : "text-gray-700 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200",
                              "h-5 w-5 shrink-0 transition-colors duration-200",
                            )}
                            aria-hidden="true"
                          />
                        )}
                        <span>{item.name}</span>
                        {item.badge && (
                          <Badge variant={item.badge.variant}>
                            {item.badge.text}
                          </Badge>
                        )}
                      </Link>
                    )}
                  </li>

                  {hasChildren && (
                    <ul
                      className={classNames(
                        "overflow-hidden transition-all duration-200 mb-2 -mt-2",
                        isExpanded ? "max-h-40" : "max-h-0",
                      )}
                    >
                      {item.children.map((child) => (
                        <li key={child.name}>
                          <Link
                            to={child.href}
                            className={classNames(
                              child.current
                                ? "bg-black/[0.025] dark:bg-white/[0.025] text-gray-700 dark:text-gray-100 text-sm font-semibold"
                                : "text-sm text-gray-600 hover:text-gray-800 hover:font-semibold dark:text-gray-300 dark:hover:text-gray-100 transition-colors duration-200",
                              "ml-4 mr-4 pl-10 rounded-md block py-1.5",
                            )}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
}
